/**
 * QuestionGenerator - Wrapper for the original question generation functionality
 */
import React, { useState, useEffect } from 'react';
import {
  Sparkles,
  Loader2,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Database,
} from 'lucide-react';
import ImageUploader from '../ImageUploader';
import QuestionPreview from '../QuestionPreview';
import { apiClient, Exam, Subject, Quiz, CourseData, Question } from '../../services/api';

const QuestionGenerator: React.FC = () => {
  // Selection state
  const [exams, setExams] = useState<Exam[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [quizzes, setQuizzes] = useState<Quiz[]>([]);

  const [selectedExam, setSelectedExam] = useState<string>('');
  const [selectedSubject, setSelectedSubject] = useState<string>('');
  const [selectedQuiz, setSelectedQuiz] = useState<string>('');

  // Image & Generation state
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [courseData, setCourseData] = useState<CourseData | null>(null);

  // UI state
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');

  // Load exams on mount
  useEffect(() => {
    const loadExams = async () => {
      setIsLoading(true);
      try {
        const examsData = await apiClient.getExams();
        setExams(examsData.exams || []);
      } catch (error) {
        console.error('Failed to load exams:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadExams();
  }, []);

  // Load subjects when exam changes
  useEffect(() => {
    if (selectedExam) {
      const loadSubjects = async () => {
        try {
          const data = await apiClient.getSubjects(selectedExam);
          setSubjects(data.subjects || []);
          setSelectedSubject('');
          setSelectedQuiz('');
          setQuizzes([]);
        } catch (error) {
          console.error('Failed to load subjects:', error);
        }
      };
      loadSubjects();
    } else {
      setSubjects([]);
      setSelectedSubject('');
    }
  }, [selectedExam]);

  // Load quizzes when subject changes
  useEffect(() => {
    if (selectedExam) {
      const loadQuizzes = async () => {
        try {
          const data = await apiClient.getQuizzes(
            selectedExam,
            selectedSubject || undefined
          );
          setQuizzes(data.quizzes || []);
          setSelectedQuiz('');
        } catch (error) {
          console.error('Failed to load quizzes:', error);
        }
      };
      loadQuizzes();
    } else {
      setQuizzes([]);
    }
  }, [selectedExam, selectedSubject]);

  // Handle course generation
  const handleGenerateCourse = async () => {
    if (!selectedImage) {
      setError('Veuillez sélectionner une image');
      return;
    }
    if (!selectedExam) {
      setError('Veuillez sélectionner un concours');
      return;
    }

    setIsGenerating(true);
    setError('');

    try {
      const result = await apiClient.generateCourseFromFile(selectedImage, {
        examId: selectedExam,
        subjectId: selectedSubject || undefined,
        quizId: selectedQuiz || undefined,
      });
      setCourseData(result);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Erreur inconnue';
      setError(`Erreur lors de la génération: ${errorMessage}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle upload to Supabase
  const handleUploadToSupabase = async () => {
    if (!courseData) return;

    const examId = courseData.metadata?.exam_id || selectedExam;
    const subjectId = courseData.metadata?.subject_id || selectedSubject;
    const quizId = courseData.metadata?.quiz_id || selectedQuiz;

    if (!examId || !subjectId || !quizId) {
      setError('IDs manquants. Veuillez sélectionner un concours, une matière et un quiz.');
      return;
    }

    setIsUploading(true);
    setError('');

    try {
      const result = await apiClient.uploadQuestions(
        courseData.questions as Question[],
        examId,
        subjectId,
        quizId
      );

      if (result.success) {
        setSuccessMessage(`✅ ${result.inserted_count} questions uploadées avec succès!`);
      } else {
        setError(result.error || "Erreur lors de l'upload");
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message;
      setError(`Erreur upload: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };

  // Reset
  const handleReset = () => {
    setSelectedImage(null);
    setCourseData(null);
    setError('');
    setSuccessMessage('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Générateur de Questions</h1>
        <p className="text-gray-500">Générez des questions à partir d'images avec GPT-4.1 Vision</p>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-700">{error}</p>
          </div>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">×</button>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-500 mt-0.5" />
          <div className="flex-1">
            <p className="text-green-700">{successMessage}</p>
          </div>
          <button onClick={handleReset} className="flex items-center gap-1 text-green-600 hover:underline">
            <RefreshCw className="w-4 h-4" />
            Nouveau
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left panel - Configuration */}
        <div className="space-y-4">
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
            <h2 className="font-semibold text-gray-900 mb-4">Configuration</h2>

            {/* Exam select */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Concours *
              </label>
              <select
                value={selectedExam}
                onChange={(e) => setSelectedExam(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={isLoading}
              >
                <option value="">Sélectionnez un concours</option>
                {exams.map((exam) => (
                  <option key={exam.id} value={exam.id}>
                    {exam.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject select */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Matière
              </label>
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={!selectedExam}
              >
                <option value="">Auto-détection</option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Quiz select */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Quiz
              </label>
              <select
                value={selectedQuiz}
                onChange={(e) => setSelectedQuiz(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={!selectedExam}
              >
                <option value="">Auto-détection</option>
                {quizzes.map((quiz) => (
                  <option key={quiz.id} value={quiz.id}>
                    {quiz.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Image upload */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
            <h2 className="font-semibold text-gray-900 mb-4">Image source</h2>
            <ImageUploader
              onImageSelect={setSelectedImage}
              selectedImage={selectedImage}
            />
          </div>

          {/* Generate button */}
          <button
            onClick={handleGenerateCourse}
            disabled={!selectedImage || !selectedExam || isGenerating}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-medium transition ${
              !selectedImage || !selectedExam || isGenerating
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Génération en cours...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Générer les questions
              </>
            )}
          </button>
        </div>

        {/* Right panel - Results */}
        <div className="lg:col-span-2">
          {courseData ? (
            <div className="space-y-4">
              <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-gray-900">
                    {courseData.questions.length} questions générées
                  </h2>
                  <div className="flex gap-2">
                    <button
                      onClick={handleUploadToSupabase}
                      disabled={isUploading}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                    >
                      {isUploading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Database className="w-4 h-4" />
                      )}
                      Upload Supabase
                    </button>
                    <button
                      onClick={handleReset}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                    >
                      Réinitialiser
                    </button>
                </div>
                </div>

                {/* Questions preview */}
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {courseData.questions.map((question, index) => (
                    <QuestionPreview key={index} question={question} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Sparkles className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Prêt à générer
              </h3>
              <p className="text-gray-500 max-w-sm mx-auto">
                Sélectionnez un concours, uploadez une image et cliquez sur "Générer" pour créer des questions.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionGenerator;
